<template>
  <div id="promotionNew">
    <PageTitle :title="$t('promotion_new.title')" :subtitle="$t('promotion_new.subtitle')"></PageTitle>

    <PageContainer>
      <div class="steps">
        <template v-for="(step, stepIndex) of steps">
          <div class="step" :key="'step' + stepIndex" :class="{ active: activeStep === stepIndex, completed: activeStep > stepIndex }">
            <div class="step__circle">
              <svgicon v-if="activeStep > stepIndex" class="circle__icon icon--check" src="icon/check-circle.svg"
                       :height="16" :width="16"></svgicon>
              <svgicon v-if="activeStep === stepIndex" class="circle__icon icon--pending" src="icon/pending.svg"
                       :height="16" :width="16"></svgicon>
            </div>
            <div class="step__text">
              {{ step }}
            </div>
          </div>
          <div class="separator" :class="{ active: stepIndex === activeStep - 1 }" v-if="stepIndex < steps.length - 1"></div>
        </template>
      </div>

      <div class="page-header">
        <div class="header__title">{{pageHeaderTitle}}</div>
        <div class="header__subtitle">{{pageHeaderSubtitle}}</div>
      </div>

      <!-- First page -->
      <template v-if="page === 0">
        <div class="promotion-types">
          <div class="top">
            <PromotionType :active="isXforY" :icon="`icon/promo/Promo-XY_${isXforY ? 'FULL' : 'OUTLINE'}.svg`"
                           @click="setAsXforY" :name="$t('promotion_new.x_for_y_title')"
                           :description="$t('promotion_new.x_for_y_description')" />
            <PromotionType :active="isProductDiscount"
                           :icon="`icon/promo/Promo-XY_${isProductDiscount ? 'FULL' : 'OUTLINE'}.svg`"
                           @click="setAsProductDiscount" :name="$t('promotion_new.product_discount_title')"
                           :description="$t('promotion_new.product_discount_description')" />
          </div>
          <div class="bottom">
            <PromotionType :active="isProductAccess"
                           :icon="`icon/promo/Promo-Access_${isProductAccess ? 'FULL' : 'OUTLINE'}.svg`"
                           @click="setAsProductAccess" :name="$t('promotion_new.product_access_title')"
                           :description="$t('promotion_new.product_access_description')" />
            <PromotionType :active="isOrderDiscount"
                           :icon="`icon/promo/Promo-Order_${isOrderDiscount ? 'FULL' : 'OUTLINE'}.svg`"
                           @click="setAsOrderDiscount" :name="$t('promotion_new.order_discount_title')"
                           :description="$t('promotion_new.order_discount_description')" />
          </div>
        </div>
        <div class="nav" v-show="!!promoType">
          <div class="grow"></div>
          <button class="btn btn-primary" @click="next(1, 1)">{{$t('next')}}</button>
        </div>
      </template>

      <template v-if="page > 0">

        <!-- X for Y configuration flow -->
        <template v-if="isXforY">

          <!-- promo configs -->
          <template v-if="page === 1">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config.png">
              </div>
              <div class="column">
                <XYParameter :promotion="promotion" />
                <br>
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
              </div>
            </div>

            <div class="nav">
              <button class="btn btn-secondary" @click="next(0, 0)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="(!hasCode || (hasCode && promotion.code))
                        && promotion.triggerParameters.y && promotion.triggerParameters.x"
                      @click="next(1, 2)">{{$t('next')}}</button>
            </div>
          </template>

          <!-- applicable tags & constraints -->
          <template v-if="page === 2">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config-2.png">
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Application :promotion="promotion" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(1, 1)">{{$t('back')}}</button>
              <button class="btn btn-primary" @click="next(2, 3)">{{$t('next')}}</button>
            </div>
          </template>

        </template>

        <!-- Product access configuration flow -->
        <template v-if="isProductAccess">
          <template v-if="page === 1">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config.png">
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <UsageRestriction :promotion="promotion" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(0, 0)">{{$t('back')}}</button>
              <button class="btn btn-primary" @click="next(1, 2)">{{$t('next')}}</button>
            </div>
          </template>

          <template v-if="page === 2">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config-2.png">
              </div>
              <div class="column">
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(1, 1)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="(!hasCode || (hasCode && promotion.code))"
                      @click="next(2, 3)">{{$t('next')}}</button>
            </div>
          </template>

        </template>

        <!-- Product discount configuration flow -->
        <template v-if="isProductDiscount">

          <template v-if="page === 1">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config.png">
              </div>
              <div class="column">
                <Tags :promotion="promotion" :tags="formattedTags" />
                <br>
                <Reward :promotion="promotion" :currencySymbol="currencySymbol" :showReplace="true" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(0, 0)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="promotion.rewardType && promotion.rewardAmount"
                      @click="next(1, 2)">{{$t('next')}}</button>
            </div>
          </template>

          <template v-if="page === 2">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config-2.png">
              </div>
              <div class="column">
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
                <br>
                <UsageRestriction :promotion="promotion" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
                <br>
                <Application :promotion="promotion" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(1, 1)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="(!hasCode || (hasCode && promotion.code))"
                      @click="next(2, 3)">{{$t('next')}}</button>
            </div>
          </template>
        </template>

        <!-- Order discount configuration flow -->
        <template v-if="isOrderDiscount">

          <template v-if="page === 1">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config.png">
              </div>
              <div class="column">
                <Reward :promotion="promotion" :currencySymbol="currencySymbol" :showReplace="false" />
                <template v-if="promotion.rewardType && promotion.rewardAmount">
                  <br>
                  <Tags :promotion="promotion" :onlyExclusion="true" :tags="formattedTags" />
                </template>
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(0, 0)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="promotion.rewardType && promotion.rewardAmount"
                      @click="next(1, 2)">{{$t('next')}}</button>
            </div>
          </template>

          <template v-if="page === 2">
            <div class="columns">
              <div class="column image-holder">
                <img class="promo__image" src="@/assets/img/promo/promo-config-2.png">
              </div>
              <div class="column">
                <Activation :promotion="promotion" @hasCode="hasCode = $event" />
                <br>
                <UsageRestriction :promotion="promotion" />
                <br>
                <MinimumAmount :promotion="promotion" :currencySymbol="currencySymbol" />
                <br>
                <Trigger :promotion="promotion" :tags="formattedTags" />
              </div>
            </div>
            <div class="nav">
              <button class="btn btn-secondary" @click="next(1, 1)">{{$t('back')}}</button>
              <button class="btn btn-primary" v-if="(!hasCode || (hasCode && promotion.code))"
                      @click="next(2, 3)">{{$t('next')}}</button>
            </div>
          </template>
        </template>

        <!-- start/end date & constraint -->
        <template v-if="page === 3">
          <div class="columns">
            <div class="column image-holder">
              <img class="promo__image" src="@/assets/img/promo/promo-calendar.png">
            </div>
            <div class="column">
              <Start :promotion="promotion" />
              <template v-if="promotion.startAt">
                <br>
                <End :promotion="promotion" />
                <template v-if="promotion.endAt">
                  <br>
                  <DisableFor :disableFor="disableFor" />
                </template>
              </template>
            </div>
          </div>
          <div class="nav">
            <button class="btn btn-secondary" @click="next(1, 2)">{{$t('back')}}</button>
            <button class="btn btn-primary" v-if="promotion.startAt && promotion.endAt"
                    @click="next(2, 4)">{{$t('next')}}</button>
          </div>
        </template>

        <!-- schedule + multi-inventory companies -->
        <template v-if="page === 4">
          <Schedule :promotion="promotion" />
          <template v-if="branchCompanies.length > 0 && !companyId">
            <br>
            <br>
            <Companies :companies="branchCompanies" :inventory="inventory" :promotion="promotion" />
          </template>
          <div class="nav">
            <button class="btn btn-secondary" @click="next(2, 3)">{{$t('back')}}</button>
            <button class="btn btn-primary" @click="next(3, 5)">{{$t('next')}}</button>
          </div>
        </template>

        <!-- marketing infos -->
        <template v-if="page === 5">
          <div class="columns">
            <div class="column image-holder">
              <img class="promo__image" src="@/assets/img/promo/promo-marketing.png">
            </div>
            <div class="column">
              <div class="group">
                <LocalizedInput :title="$t('name')" :translate="promotion.name"></LocalizedInput>
              </div>
              <br>
              <div class="group">
                <LocalizedInput :title="$t('description')" :translate="promotion.description"></LocalizedInput>
              </div>
              <br>
              <ImageUpload identifier="promoImage" :previewHeight="110"
                           :object="promotion.image" @changed="promotion.image = $event" :maxSizeInMb="0.8"></ImageUpload>
              <br>
              <div class="group">
                <label>{{$t('external_id')}}</label>
                <input type="text" v-model="promotion.externalId">
              </div>
            </div>
          </div>
          <div class="nav">
            <button class="btn btn-secondary" @click="next(2, 4)">{{$t('back')}}</button>
            <button class="btn btn-primary" v-if="$to(promotion.name)" @click="next(4, 6)">{{$t('next')}}</button>
          </div>
        </template>

        <!-- promo launch confirmation -->
        <template v-if="page === 6">
          <div class="columns">
            <div class="column image-holder">
              <img class="promo__image" src="@/assets/img/promo/promo-launch.png">
            </div>
            <div class="column">
              <div class="launch-box">
                <div class="launch-box__icon">
                  <svgicon src="icon/tag.svg" :height="16" :width="16"></svgicon>
                </div>
                <div class="launch-box__content">
                  <div class="launch-box__title">{{$t('promotion_new.promotion_type')}}</div>
                  <div v-if="isXforY" v-html="$t('promotion_new.launch_text.xy')
                      .replace('{NAME}', $to(promotion.name))
                      .replace('{X}', promotion.triggerParameters.x)
                      .replace('{Y}', promotion.triggerParameters.y)">
                  </div>
                  <div v-if="isOrderDiscount" v-html="$t('promotion_new.launch_text.order_discount')
                      .replace('{NAME}', $to(promotion.name))
                      .replace('{VALUE}', promotionRewardValue)"></div>
                  <div v-if="isProductAccess" v-html="$t('promotion_new.launch_text.product_access')
                      .replace('{NAME}', $to(promotion.name))"></div>
                  <div v-if="isProductDiscount" v-html="$t('promotion_new.launch_text.product_discount')
                      .replace('{NAME}', $to(promotion.name))
                      .replace('{VALUE}', promotionRewardValue)"></div>
                </div>
              </div>
              <div class="launch-box">
                <div class="launch-box__icon">
                  <svgicon src="icon/date-range.svg" :height="16" :width="16"></svgicon>
                </div>
                <div class="launch-box__content">
                  <div class="launch-box__title">{{$t('promotion_new.availability')}}</div>
                  <div v-html="$t('promotion_new.launch_text.dates')
                    .replace('{START}', moment(promotion.startAt).format('LLL'))
                    .replace('{END}', moment(promotion.endAt).format('LLL'))">
                  </div>
                </div>
              </div>
              <div class="launch-box" v-if="hasCode">
                <div class="launch-box__icon">
                  <svgicon src="icon/lock.svg" :height="16" :width="16"></svgicon>
                </div>
                <div class="launch-box__content">
                  <div class="launch-box__title">{{$t('promotion_new.access')}}</div>
                  <div v-html="$t('promotion_new.launch_text.code').replace('{CODE}', promotion.code)">
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="nav">
            <button class="btn btn-secondary" @click="next(3, 5)">{{$t('back')}}</button>
            <div class="grow"></div>
            <button class="btn btn-primary" @click="launch">{{$t('promotion_new.launch')}}</button>
          </div>
        </template>

        <template v-if="page === 7">
          <div class="columns">
            <div class="column image-holder">
              <img class="promo__image" src="@/assets/img/promo/promo-finish.png">
            </div>
            <div class="column">
              <div class="finish-action">
                <div class="finish-action__icon">
                  <svgicon src="icon/promo/Promo_OUTLINE.svg" :height="80" :width="80"></svgicon>
                </div>
                <div class="finish-action__content">
                  <div class="finish-action__title">{{$t('promotions.title')}}</div>
                  <div class="finish-action__description">{{$t('promotions.description')}}</div>
                  <button class="btn btn-primary" @click="goToPromotions">
                    <svgicon src="icon/arrow-right-line.svg" :height="12" :width="12"></svgicon>
                    {{$t('access')}}
                  </button>
                </div>
              </div>
              <div class="finish-action">
                <div class="finish-action__icon">
                  <svgicon src="icon/promo/Promo-Popup_OUTLINE.svg" :height="80" :width="80"></svgicon>
                </div>
                <div class="finish-action__content">
                  <div class="finish-action__title">{{$t('promotions.modal.title')}}</div>
                  <div class="finish-action__description">{{$t('promotions.modal.modal_description')}}</div>
                  <button class="btn btn-primary" @click="goToPromoPopup">
                    <svgicon src="icon/arrow-right-line.svg" :height="12" :width="12"></svgicon>
                    {{$t('access')}}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="nav">
            <div class="grow"></div>
            <button class="btn btn-primary" @click="back">{{$t('exit')}}</button>
          </div>
        </template>

      </template>
    </PageContainer>

    <PageFooter :backAction="{ action: back, text: $t('promotion_new.back') }"></PageFooter>

  </div>
</template>

<script>
  import PageContainer from "@/components/PageContainer";
  import PageTitle from "@/components/PageTitle";
  import PageFooter from "@/components/PageFooter";
  import LocalizedInput from "@/components/tools/LocalizedInput";
  import ImageUpload from '@/components/tools/ImageUpload.vue';
  import { mapActions } from "vuex";
  import { Core } from "@/core/Core";
  import moment from "moment";
  import XYParameter from "@/components/promotion/XYParameters";
  import Activation from "@/components/promotion/Activation";
  import Application from "@/components/promotion/Application";
  import Tags from "@/components/promotion/Tags";
  import UsageRestriction from "@/components/promotion/UsageRestriction";
  import MinimumAmount from "@/components/promotion/MinimumAmount";
  import Trigger from "@/components/promotion/Trigger";
  import Reward from "@/components/promotion/Reward";
  import Start from "@/components/promotion/Start";
  import End from "@/components/promotion/End";
  import DisableFor from "@/components/promotion/DisableFor";
  import Schedule from "@/components/promotion/Schedule";
  import Companies from "@/components/promotion/Companies";
  import PromotionType from "@/components/promotion/PromotionType";

  export default {
    name: "PromotionNew",

    components: {
      Activation, XYParameter, Tags, UsageRestriction, MinimumAmount, PromotionType,
      Trigger, Reward, Start, End, DisableFor, Schedule, Companies, Application,
      PageTitle, PageContainer, PageFooter, LocalizedInput, ImageUpload
    },

    data() {
      return {
        X_FOR_Y: "x_for_y",
        PRODUCT_ACCESS: "product_access",
        PRODUCT_DISCOUNT: "product_discount",
        ORDER_DISCOUNT: "order_discount",
        activeStep: 0,
        page: 0,
        promoType: null,
        currencySymbol: null,
        showSchedule: false,
        hasCode: false,
        steps: [
          this.$t('promotion_new.promotion_type'),
          this.$t('promotion_new.configuration'),
          this.$t('promotion_new.availability'),
          this.$t('promotion_new.marketing'),
          this.$t('promotion_new.launch'),
        ],
        tags: [],
        branchCompanies: [],
        inventory: null,
        disableFor: {
          delivery: false,
          takeout: false
        },
        promotion: {
          id: null,
          companies: [],
          externalId: null,
          image: { url: "" },
          name: { fr: "", en: "" },
          description: { fr: "", en: "" },
          inclusion: {
            tags: []
          },
          exclusion: {
            tags: []
          },
          rewardType: "",
          rewardAmount: null,
          minAmount: null,
          schedule: null,
          usageRestriction: "unlimited",
          triggerType: "",
          triggerParameters: {
            x: null,
            y: null,
            hideReminders: false,
            tags: [],
            hideItems: false,
            items: []
          },
          startAt: null,
          endAt: null,
          active: true,
          deleted: false,
          code: null,
          appliedOn: null,
          disableForMethod: [],
          version: 1
        },
        moment
      }
    },

    computed: {
      pageHeaderTitle() {
        switch (this.page) {
          case 0:
            return this.$t('promotion_new.promotion_type');
          case 1:
          case 2:
            return this.$t(`promotion_new.${this.promoType}_configuration_title`);
          case 3:
          case 4:
            return this.$t('promotion_new.availability');
          case 5:
            return this.$t('promotion_new.marketing');
          case 6:
            return this.$t('promotion_new.launch');
          case 7:
            return this.$t('promotion_new.congratulations');
        }
      },
      pageHeaderSubtitle() {
        switch (this.page) {
          case 0:
            return this.$t('promotion_new.promotion_type_subtitle');
          case 1:
          case 2:
            return this.$t(`promotion_new.${this.promoType}_configuration_subtitle`);
          case 3:
            return this.$t('promotion_new.availability_subtitle');
          case 4:
            return this.$t("promotions.schedule_explanation");
          case 5:
            return this.$t("promotion_new.marketing_description");
          case 6:
            return this.$t("promotion_new.launch_description");
          case 7:
            return this.$t('promotion_new.congratulations_description');
        }
      },
      promotionRewardValue() {
        if (this.promotion.rewardType === "$") {
          return `${this.$t('promotion_new.discount_of')} ${this.$tc(parseInt(this.promotion.rewardAmount), this.inventory.currency)}`;
        }
        if (this.promotion.rewardType === "replace") {
          return `${this.$t('promotion_new.new_price_of')} ${this.$tc(parseInt(this.promotion.rewardAmount), this.inventory.currency)}`;
        }
        if (this.promotion.rewardType === "%") {
          return `${this.$t('promotion_new.discount_of')} ${this.promotion.rewardAmount}%`;
        }
      },
      formattedTags() {
        return this.tags.map(t => {
          return {
            id: t.id,
            label: t.name,
            value: t.id
          };
        });
      },
      branchId() {
        return this.$route.query.branch;
      },
      companyId() {
        return this.$route.query.company;
      },
      isXforY() {
        return this.promoType === this.X_FOR_Y;
      },
      isProductAccess() {
        return this.promoType === this.PRODUCT_ACCESS;
      },
      isProductDiscount() {
        return this.promoType === this.PRODUCT_DISCOUNT;
      },
      isOrderDiscount() {
        return this.promoType ===  this.ORDER_DISCOUNT;
      },
      isPromotionScheduleEmpty() {
        if (!this.promotion.schedule) {
          return true;
        }
        let notEmptyDays = this.promotion.schedule.filter(d =>
            d.hours.length > 0 && d.hours.filter(h => !!h.from || !!h.to).length > 0);
        return notEmptyDays.length === 0;
      },
    },

    async mounted() {
      showSpinner();
      this.$store.state.pageTitleTranslationKey = "promotion_new.title";
      this.$store.state.pageIcon = "/icon/promos.svg";
      this.inventory = await this.$store.getters.getContextInventory;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.createSchedule();
      await this.loadTags();
      await this.loadBranchCompanies();
      hideSpinner();
    },

    methods: {
      ...mapActions(["getManyInventoryEntity", "createPromotion"]),

      async loadTags() {
        this.tags = await this.getManyInventoryEntity({entity: "tags", inventory: this.inventory.id});
      },
      async loadBranchCompanies() {
        if (this.companyId) {
          this.branchCompanies = [];
        } else {
          this.branchCompanies = await Core.request('GET', '/api/entity/company/select', {branch: this.branchId});
          if (this.branchCompanies.length > 0) {
            this.branchCompanies = this.branchCompanies.filter(c => c.inventory == this.inventory.id);
          }
        }
      },
      async launch() {
        let copy = this._formatBeforeSave(this.promotion);
        showSpinner();
        let response = await this.createPromotion({ inventory: this.inventory.id, data: copy });
        hideSpinner();
        if (!response) {
          toast(this.$t("promotions.error.save"), "error");
          return;
        }
        this.next(6, 7);
      },
      _formatBeforeSave(data) {
        let copy = deepCopy(data);

        copy.image = getImageData(data.image);
        if (copy.startAt) {
          copy.startAt = moment(copy.startAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (copy.endAt) {
          copy.endAt = moment(copy.endAt).format("YYYY-MM-DD HH:mm:ss");
        }
        if (!copy.schedule || this.isPromotionScheduleEmpty) {
          copy.schedule = null;
        }
        if (copy.inclusion.tags) {
          copy.inclusion.tags = copy.inclusion.tags.map(x => x.id);
        }
        if (copy.exclusion.tags) {
          copy.exclusion.tags = copy.exclusion.tags.map(x => x.id);
        }
        if (copy.rewardType === "hidden_product") {
          copy.rewardAmount = 0;
        }
        copy.minAmount = copy.minAmount || 0;
        if (copy.triggerParameters.x) {
          copy.triggerParameters.x = parseInt(copy.triggerParameters.x);
        }
        if (!this.companyId) {
          copy.companies = copy.companies.map(x => x.id);
        } else {
          delete copy.companies;
        }
        if (copy.triggerParameters.y) {
          copy.triggerParameters.y = parseInt(copy.triggerParameters.y);
        }
        if (copy.triggerParameters.items) {
          copy.triggerParameters.items = copy.triggerParameters.items.map(x => x.id);
        }
        if (copy.triggerParameters.tags) {
          copy.triggerParameters.tags = copy.triggerParameters.tags.map(x => x.id);
        }
        copy.rewardAmount = parseFloat(String(copy.rewardAmount).replace(",", "."));
        // Disable for methods
        copy.disableForMethod = [];
        if (this.disableFor.takeout) {
          copy.disableForMethod.push("takeout");
        }
        if (this.disableFor.delivery) {
          copy.disableForMethod.push("delivery");
        }
        return copy;
      },
      goToPromotions() {
        this.$router.push(this.$router.buildContext("/promotions"));
      },
      goToPromoPopup() {
        this.$router.push(this.$router.buildContext("/promotion-modal"));
      },
      createSchedule() {
        this.promotion.schedule = [
          { name: "monday", hours: [] },
          { name: "tuesday", hours: [] },
          { name: "wednesday", hours: [] },
          { name: "thursday", hours: [] },
          { name: "friday", hours: [] },
          { name: "saturday", hours: [] },
          { name: "sunday", hours: [] }
        ]
      },
      setAsXforY() {
        this.promotion.rewardType = "%";
        this.promotion.rewardAmount = "100";
        this.promotion.triggerType = "x_for_y";
        this.promotion.appliedOn = "items"; //items or one_item
        this.promotion.triggerParameters.x = 2;
        this.promotion.triggerParameters.y = 1;
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.X_FOR_Y;
      },
      setAsProductAccess() {
        this.promotion.appliedOn = "items";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.rewardType = "hidden_product";
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.PRODUCT_ACCESS;
      },
      setAsProductDiscount() {
        this.promotion.appliedOn = "items"; //items or one_item
        this.promotion.rewardAmount = "";
        this.promotion.rewardType = "";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.PRODUCT_DISCOUNT;
      },
      setAsOrderDiscount() {
        this.promotion.appliedOn = "sale";
        this.promotion.triggerType = ""; //mobile or 'with_item'
        this.promotion.rewardAmount = "";
        this.promotion.rewardType = "";
        this.promotion.inclusion.tags = [];
        this.promotion.exclusion.tags = [];
        this.promotion.triggerParameters.tags = [];

        this.promoType = this.ORDER_DISCOUNT;
      },
      next(activeStep, page) {
        this.activeStep = activeStep;
        this.page = page;
      },
      back() {
        this.$router.push(this.$router.buildContext("/promotions"));
      }
    }
  }
</script>

<style lang="scss" scoped>
  #promotionNew {
    display: flex;
    flex-direction: column;
    padding-bottom: 0;
  }
  .page-container {
    padding: 48px;
    display: flex;
    flex-direction: column;
  }
  .nav {
    padding-top: 32px;
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    flex-grow: 1;
  }
  .steps {
    display: flex;
    justify-content: center;
    padding-bottom: 48px;

    .separator {
      height: 4px;
      width: 100%;
      max-width: 100px;
      background-color: #f5f5f5;
      margin-top: 14px;

      &.active {
        background: linear-gradient(90deg, #f5f5f5, #fc6650);
      }
    }

    .step {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: space-between;
      text-align: center;

      &:not(.completed):not(.active) {
        .step__circle {
          width: 16px;
          height: 16px;
          margin-top: 8px;
          border: 2px solid #f5f5f5;
          background-color: white;
        }
      }

      .step__circle {
        border-radius: 100%;
        margin-bottom: 16px;
      }

      .step__text {
        font-size: 12px;
        font-weight: bold;
        color: #999999;
        width: 0px;
        white-space: nowrap;
        display: flex;
        justify-content: center;
      }

      &.completed {
        .step__circle {
          background-color: #f5f5f5;
          fill: #fc6650;
          padding: 8px;
        }
      }

      &.active {
        .step__circle {
          background-color: #fc6650;
          fill: white;
          padding: 8px;
        }
        .step__text {
          color: #fc6650;
        }
      }
    }
  }
  .page-header {
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    margin-bottom: 32px;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;

    .header__title {
      font-size: 14px;
      color: #404040;
      font-weight: bold;
    }
    .header__subtitle {
      font-size: 12px;
      color: #808080;
    }
  }

  .image-holder {
    align-items: center;
    justify-content: center;
  }

  .promo__image {
    width: 100%;
    height: auto;
    max-width: 350px;
  }

  .promotion-types {
    display: flex;
    flex-direction: column;
    .top, .bottom {
      display: flex;
    }
    .top {
      margin-bottom: 32px;
    }
  }
  .finish-action {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    padding: 8px;
    display: flex;
    border-radius: 16px;

    &:not(:last-child) {
      margin-bottom: 20px;
    }

    .finish-action__icon {
      width: 140px;
      height: 140px;
      flex-shrink: 0;
      background-color: #f5f5f5;
      fill: #fc6650;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 8px;
      margin-right: 24px;
    }
    .finish-action__title {
      font-weight: bold;
      font-size: 20px;
      color: #1a1a1a;
      margin-bottom: 5px;
    }
    .finish-action__description {
      color: #808080;
      font-size: 14px;
      margin-bottom: 20px;
    }
    .btn {
      display: flex;
      align-items: center;

      .svgicon {
        fill: white;
        margin-right: 12px;
      }
    }
  }
  .launch-box {
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
    padding: 12px;
    border-radius: 8px;
    display: flex;

    &:not(:last-child) {
      margin-bottom: 48px;
    }

    .launch-box__title {
      color: #fc6650;
      font-weight: bold;
      font-size: 12px;
      margin-bottom: 5px;
    }

    .launch-box__icon {
      height: 32px;
      width: 32px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: #f5f5f5;
      flex-shrink: 0;
      margin-right: 12px;
      fill: #ccc;
    }
  }

  @media screen and (max-width: 800px) {
    #promotionNew {
      .page-container {
        padding: 24px;
      }
      .steps {
        padding-bottom: 30px;
      }
      .steps .step {
        .step__text {
          font-size: 11px;
          white-space: initial;
          height: 26px;
        }
        .step__circle {
          margin-bottom: 5px;
        }
      }
      .page-header {
        margin-bottom: 16px;
      }
      .launch-box {
        margin-bottom: 16px;
      }
      .promo__image {
        max-width: 230px;
        margin-bottom: 16px;
      }
      .promotion-types {
        .top, .bottom {
          flex-direction: column;
          margin-bottom: 0;

          .promotion-type {
            width: 100%;
            margin-right: 0;
            margin-bottom: 10px;
          }
        }
      }
      .finish-action {
        .finish-action__icon {
          height: 120px;
          width: 120px;
          margin-right: 16px;
        }
        .finish-action__description {
          margin-bottom: 10px;
          font-size: 13px;
        }
        .finish-action__title {
          font-size: 18px;
        }
      }
    }
  }
</style>

<style lang="scss">
  #promotionNew, #promotionEdit {
    .vdatetime input {
      width: 100%;
    }
    .toggle-switch {
      border-radius: 8px;
      font-size: 14px;
      padding: 6px 16px;
      justify-content: space-between;
      font-weight: bold;
      box-shadow: 0 1px 2px rgba(0,0,0,0.12);

      &:not(:last-child) {
         margin-bottom: 10px;
      }

      .text {
        flex-grow: 1;

      }

      &.thin {
        box-shadow: none;
        font-weight: normal;
        font-size: 14px;
        padding: 0;
        justify-content: flex-start;
        .text {
          flex-grow: initial;
          padding-right: 32px;
          color: #1A1A1A;
        }
      }

      .toggle-description {
        font-size: 12px;
        color: #808080;
      }
    }

    .input-group {
      position: relative;
      input {
        padding: 8px 2px 8px 8px;
        margin-left: 10px;
        width: 100%;
      }

      .input-overlay {
        position: absolute;
        right: 16px;
        top: 12px;
        font-size: 12px;
        color: #808080;
        font-weight: bold;
        z-index: 1;
      }
    }

    .radios {
      display: flex;

      .radio {
        width: 50%;
        flex-basis: 0;
        label:not(:last-child) {
          margin-bottom: 8px;
        }
      }
    }

    .question {
      margin-bottom: 15px;
      display: flex;
      font-size: 14px;
      align-items: center;

      .tooltip {
        margin-left: 15px;
      }

      &:not(:first-child) {
        margin-top: 32px;
      }
    }

    .column {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      font-size: 14px;

      label {
        font-size: 14px;
        padding-right: 24px;
        white-space: nowrap;
      }
    }
  }

  @media screen and (max-width: 800px) {
    .promotion-types .promotion-type {
      .promotion-type__icon {
        padding: 16px;
        margin-right: 16px;
      }
      .promotion-type__name {
        font-size: 18px;
      }
    }
  }
</style>